"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { Loader2, FlaskConical } from "lucide-react"

interface TestControlDialogProps {
  isOpen: boolean
  onClose: () => void
  controlId: string
  controlName: string
  riskId: string
  assignmentId: string
  onTestCreated?: () => void
  isEditMode?: boolean
  existingTestData?: any
}

interface User {
  id: string
  name: string
  email: string
  role: string
}

export default function TestControlDialog({
  isOpen,
  onClose,
  controlId,
  controlName,
  riskId,
  assignmentId,
  onTestCreated,
  isEditMode = false,
  existingTestData
}: TestControlDialogProps) {
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [users, setUsers] = useState<User[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)

  const [formData, setFormData] = useState({
    testDate: new Date().toISOString().split('T')[0], // Today's date
    testedBy: "",
    testingMethod: "",
    sampleSize: "",
    exceptionsFound: "0",
    testResult: "",
    testingNotes: ""
  })

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      if (isEditMode && existingTestData) {
        fetchExistingTestData()
      } else {
        // Reset form for new test
        setFormData({
          testDate: new Date().toISOString().split('T')[0],
          testedBy: "",
          testingMethod: "",
          sampleSize: "",
          exceptionsFound: "0",
          testResult: "",
          testingNotes: ""
        })
      }
      fetchUsers()
    }
  }, [isOpen, isEditMode, existingTestData])

  const fetchExistingTestData = async () => {
    try {
      const response = await fetch(`/api/control-tests?assignmentId=${assignmentId}&controlId=${controlId}&riskId=${riskId}`)
      if (response.ok) {
        const tests = await response.json()
        if (tests.length > 0) {
          const latestTest = tests.sort((a: any, b: any) => 
            new Date(b.TestDate).getTime() - new Date(a.TestDate).getTime()
          )[0]
          
          setFormData({
            testDate: latestTest.TestDate,
            testedBy: latestTest.TestedBy.toString(),
            testingMethod: latestTest.TestingMethod || "",
            sampleSize: latestTest.SampleSize ? latestTest.SampleSize.toString() : "",
            exceptionsFound: latestTest.ExceptionsFound ? latestTest.ExceptionsFound.toString() : "0",
            testResult: latestTest.TestResult || "",
            testingNotes: latestTest.TestingNotes || ""
          })
        }
      }
    } catch (error) {
      console.error('Error fetching existing test data:', error)
    }
  }

  // Fetch users when dialog opens
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true)
      const response = await fetch('/api/users')
      if (response.ok) {
        const data = await response.json()
        setUsers(data)
      }
    } catch (error) {
      console.error('Error fetching users:', error)
      toast({
        title: "Error",
        description: "Failed to load users",
        variant: "destructive"
      })
    } finally {
      setLoadingUsers(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validate required fields
    if (!formData.testDate || !formData.testedBy) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields (Test Date and Tested By)",
        variant: "destructive"
      })
      return
    }

    setIsSubmitting(true)

    try {
      const url = '/api/control-tests'
      const method = isEditMode ? 'PUT' : 'POST'
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          controlId,
          riskId,
          assignmentId,
          testDate: formData.testDate,
          testedBy: parseInt(formData.testedBy),
          testingMethod: formData.testingMethod || null,
          sampleSize: formData.sampleSize ? parseInt(formData.sampleSize) : null,
          exceptionsFound: parseInt(formData.exceptionsFound),
          testResult: formData.testResult || null,
          testingNotes: formData.testingNotes || null
        })
      })

      if (!response.ok) {
        throw new Error(`Failed to ${isEditMode ? 'update' : 'create'} control test`)
      }

      const result = await response.json()

      toast({
        title: "Success",
        description: `Control test ${isEditMode ? 'updated' : 'recorded'} successfully`,
      })

      // Reset form
      setFormData({
        testDate: new Date().toISOString().split('T')[0],
        testedBy: "",
        testingMethod: "",
        sampleSize: "",
        exceptionsFound: "0",
        testResult: "",
        testingNotes: ""
      })

      // Close dialog first, then call callback to refresh parent component
      onClose()
      
      // Call callback to refresh UI after successful save
      if (onTestCreated) {
        await onTestCreated()
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} control test:`, error)
      toast({
        title: "Error",
        description: `Failed to ${isEditMode ? 'update' : 'create'} control test. Please try again.`,
        variant: "destructive"
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      testDate: new Date().toISOString().split('T')[0],
      testedBy: "",
      testingMethod: "",
      sampleSize: "",
      exceptionsFound: "0",
      testResult: "",
      testingNotes: ""
    })
    onClose()
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-blue-600" />
            {isEditMode ? "Edit Control Test" : "Test Control"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode ? "Update test results for: " : "Record test results for: "}
            <span className="font-semibold">{controlName}</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {/* Test Date */}
            <div className="space-y-2">
              <Label htmlFor="testDate" className="required">
                Test Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="testDate"
                type="date"
                value={formData.testDate}
                onChange={(e) => setFormData({ ...formData, testDate: e.target.value })}
                required
              />
            </div>

            {/* Tested By */}
            <div className="space-y-2">
              <Label htmlFor="testedBy" className="required">
                Tested By <span className="text-red-500">*</span>
              </Label>
              {loadingUsers ? (
                <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading users...
                </div>
              ) : (
                <Select
                  value={formData.testedBy}
                  onValueChange={(value) => setFormData({ ...formData, testedBy: value })}
                  required
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tester" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.length > 0 ? (
                      users.map((user) => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name}
                        </SelectItem>
                      ))
                    ) : (
                      <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                        No users available
                      </div>
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Testing Method */}
            <div className="space-y-2">
              <Label htmlFor="testingMethod">Testing Method</Label>
              <Select
                value={formData.testingMethod}
                onValueChange={(value) => setFormData({ ...formData, testingMethod: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select testing method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Inquiry">Inquiry</SelectItem>
                  <SelectItem value="Observation">Observation</SelectItem>
                  <SelectItem value="Inspection">Inspection</SelectItem>
                  <SelectItem value="Reperformance">Reperformance</SelectItem>
                  <SelectItem value="Walkthrough">Walkthrough</SelectItem>
                  <SelectItem value="Analytical">Analytical Procedures</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Sample Size */}
              <div className="space-y-2">
                <Label htmlFor="sampleSize">Sample Size</Label>
                <Input
                  id="sampleSize"
                  type="number"
                  min="0"
                  value={formData.sampleSize}
                  onChange={(e) => setFormData({ ...formData, sampleSize: e.target.value })}
                  placeholder="Enter sample size"
                />
              </div>

              {/* Exceptions Found */}
              <div className="space-y-2">
                <Label htmlFor="exceptionsFound">Exceptions Found</Label>
                <Input
                  id="exceptionsFound"
                  type="number"
                  min="0"
                  value={formData.exceptionsFound}
                  onChange={(e) => setFormData({ ...formData, exceptionsFound: e.target.value })}
                  placeholder="Number of exceptions"
                />
              </div>
            </div>

            {/* Test Result */}
            <div className="space-y-2">
              <Label htmlFor="testResult">Test Result</Label>
              <Select
                value={formData.testResult}
                onValueChange={(value) => setFormData({ ...formData, testResult: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select test result" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Passed">Passed</SelectItem>
                  <SelectItem value="Failed">Failed</SelectItem>
                  <SelectItem value="Passed with Exceptions">Passed with Exceptions</SelectItem>
                  <SelectItem value="Not Applicable">Not Applicable</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Testing Notes */}
            <div className="space-y-2">
              <Label htmlFor="testingNotes">Testing Notes</Label>
              <Textarea
                id="testingNotes"
                value={formData.testingNotes}
                onChange={(e) => setFormData({ ...formData, testingNotes: e.target.value })}
                placeholder="Enter any additional notes or observations..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditMode ? "Updating..." : "Saving..."}
                </>
              ) : (
                isEditMode ? "Update Test" : "Save Test"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

